import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import { IMAGES } from "@/lib/images";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[700px] -mt-20 overflow-hidden grain bg-ink" data-testid="home-hero">
        <div className="absolute inset-0">
          {/* Video plays on desktop/non-data-saver; falls back to poster image on iOS Low Power etc. */}
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={IMAGES.heroHome}
            preload="metadata"
            className="w-full h-full object-cover scale-105"
            data-testid="home-hero-video"
          >
            <source src="/videos/hero-hawk.webm" type="video/webm" />
            <source src="/videos/hero-hawk-1280.mp4" media="(max-width: 1024px)" type="video/mp4" />
            <source src="/videos/hero-hawk.mp4" type="video/mp4" />
            {/* Fallback for browsers that don't support video */}
            <img src={IMAGES.heroHome} alt="Sunset over Iron Talon Farm" className="w-full h-full object-cover" />
          </video>
          {/* Soft vignette to draw the eye toward center */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,_transparent_40%,_rgba(14,14,14,0.6)_100%)]" />
          {/* Bottom gradient for legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink/85" />
        </div>

        <div className="relative h-full container-x flex flex-col justify-end pb-24 lg:pb-36">
          <FadeIn>
            <p className="overline text-cream/85 mb-6 tracking-[0.32em]" data-testid="home-hero-eyebrow">
              <span className="inline-block w-8 h-px bg-cream/50 align-middle mr-4" />
              Kempton, Pennsylvania · 50 acres · Est. mid 1800s
            </p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1
              data-testid="home-hero-headline"
              className="font-serif text-cream text-[48px] sm:text-6xl lg:text-[96px] font-light leading-[0.95] tracking-tight max-w-5xl drop-shadow-[0_2px_24px_rgba(0,0,0,0.4)]"
            >
              <span>A sanctuary where</span>
              <br />
              <span className="italic text-cream/90">iron sharpens iron.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={300}>
            <p className="mt-10 max-w-xl text-cream/85 text-lg leading-[1.7] font-light drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]">
              Six historic cottages, mountain views, and quiet places to breathe —
              a working farm and retreat built around hospitality, restoration, and service.
            </p>
          </FadeIn>
          <FadeIn delay={460}>
            <div className="mt-12 flex flex-wrap gap-4">
            </div>
          </FadeIn>

          {/* Scroll cue */}
          <div className="hidden lg:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-3 text-cream/50 text-[10px] tracking-[0.32em] uppercase pointer-events-none">
            <span>Scroll</span>
            <span className="block w-px h-12 bg-cream/30" />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-narrow py-28 lg:py-36" data-testid="home-intro">
        <FadeIn><p className="overline mb-8 text-center">Welcome</p></FadeIn>
        <FadeIn delay={120}>
          <h2 className="font-serif text-3xl lg:text-5xl text-moss font-light leading-[1.15] tracking-tight text-center">
            A quiet, scenic haven in the
            <br className="hidden md:block" /> hills of <em className="not-italic text-clay">Pennsylvania</em>.
          </h2>
        </FadeIn>
        <FadeIn delay={260}>
          <p className="mt-10 text-lg lg:text-xl text-coal/70 leading-[1.75] max-w-2xl mx-auto text-center">
            Iron Talon Farm is a private retreat property rooted in restoration, connection, and renewal.
            Through our cottages and our wellness collective, we are building a place where people can come
            as they are, breathe deeply, and leave more grounded than when they arrived.
          </p>
        </FadeIn>
      </section>

      {/* NAVIGATE — big dynamic hand-off to the rest of the site */}
      <section className="bg-oat py-24 lg:py-32" data-testid="home-navigate">
        <div className="container-x">
          <FadeIn>
            <p className="overline mb-6 text-center">Find your way in</p>
            <h2 className="font-serif text-4xl lg:text-6xl text-moss font-light leading-[1.05] tracking-tight text-center max-w-3xl mx-auto">
              Wherever you're headed, <em className="not-italic text-clay">start here</em>.
            </h2>
          </FadeIn>

          {/* Primary — four big cards for the main paths */}
          <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {[
              { tag: "About", title: "About the Farm", desc: "The land, the story, the Stewards. Walk the property before you visit.", to: "/farm", img: IMAGES.imgFarmBanner },
              { tag: "Stay", title: "Plan a Stay", desc: "Six historic cottages. Off-grid hilltop, creekside porch, pond views, restored barn.", to: "/stay", img: IMAGES.imgOverviewHero },
              { tag: "Serve", title: "The Shadow Foundation", desc: "Quiet retreats for military, law enforcement, and first responders — offered with dignity.", to: "/shadow-foundation", img: IMAGES.imgHillDope },
              { tag: "Rest", title: "Find Healing & Wellness at New Leaf", desc: "Restorative workshops and small group retreats. Coming soon — sign up to be first to hear when.", to: "/new-leaf", img: IMAGES.imgRestFlower, comingSoon: true },
            ].map((c, i) => (
              <FadeIn key={c.title} delay={i * 100}>
                <Link
                  to={c.to}
                  data-testid={`home-nav-primary-${c.tag.toLowerCase()}`}
                  className="group relative block bg-cream overflow-hidden h-full transition-shadow duration-500 hover:shadow-2xl"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                  </div>
                  <div className="p-8 lg:p-10">
                    <p className="overline text-clay mb-3">{c.tag}</p>
                    <h3 className="font-serif text-2xl lg:text-[26px] text-moss font-medium leading-tight group-hover:text-clay transition-colors">
                      {c.title}
                      {c.comingSoon && <span className="ml-2 text-xs uppercase tracking-widest text-clay/70 font-sans font-normal align-middle">(coming soon)</span>}
                    </h3>
                    <p className="mt-4 text-coal/70 leading-[1.75] text-base">{c.desc}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-moss group-hover:text-clay transition-colors">
                      Explore
                      <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
